import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useWebsiteData } from '../hooks/useWebsiteData';
import { usePageMeta } from '../hooks/usePageMeta';
import { HeroImage } from '../components/HeroImage';
import FormExample from '../components/FormExample';
import { ScrollPortfolio, TSection } from '../components/ScrollPortfolio';
import Footer from '@components/Footer';
import { PortfolioSmallLayout } from '@components/PortfolioSmall';
import { useBreakpointValue } from '@hooks/useBreakpointValue';
import { HeroSmall } from '@components/HeroSmall';
import { useRef } from 'react';
export const Route = createFileRoute('/portfolio/')({
  component: PortfolioComponent,
});

function PortfolioComponent() {
  usePageMeta(
    'Portfolio | Boyden Renovations',
    "Browse Boyden Renovations' portfolio of completed home renovation and remodeling projects."
  );

  const { data: websiteData, isLoading, error } = useWebsiteData();
  const contactFromRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const isMediumUp = useBreakpointValue({
    base: false,
    md: true,
  });
  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-slate-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 font-semibold">Error loading portfolios</p>
          <p className="text-red-500 text-sm mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!websiteData || Object.keys(websiteData).length === 0) {
    return (
      <div className="p-8">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
          <p className="text-slate-600">No portfolios found</p>
        </div>
      </div>
    );
  }

  // Transform websiteData into sections array
  const sections: TSection[] = Object.keys(websiteData)
    .map((portfolioId) => {
      const section = websiteData[portfolioId];
      return {
        id: portfolioId,
        name:
          section._images?.[0]?.metadata?.website ??
          section._metadata?.name.replace(/_/g, ' '),
        originalName: section._metadata?.name,
        imageUrl:
          (
            section._images?.find(
              (img: { context?: { Hero?: string } }) =>
                img.context?.Hero === 'true'
            ) ?? section._images?.[0]
          )?.secure_url || '',
      };
    })
    .filter((section) => section.imageUrl); // Only include sections with images

  const handleSectionClick = (sectionId: string) => {
    navigate({
      to: '/portfolio/$portfolioId',
      params: { portfolioId: sectionId },
    });
  };

  return (
    <div>
      <HeroImage
        imageUrl="https://res.cloudinary.com/dtgkdtn4o/image/upload/v1777656421/Website/Hoover/LGphoto-2.webp"
        title="Portfolio"
        overlayOpacity={0.2}
      />

      {isMediumUp ? (
        <>
          <div className="w-full h-12" />
          <ScrollPortfolio
            sections={sections}
            onSectionClick={handleSectionClick}
          />
        </>
      ) : (
        <PortfolioSmallLayout
          sections={sections}
          onSectionClick={handleSectionClick}
        />
      )}
      <HeroSmall
        imageUrl="/magree-4674.webp"
        title="Start Your Project"
        subtitle="Let's bring your vision to reality."
        cta={() => {
          if (contactFromRef.current) {
            window.scrollTo({
              top: contactFromRef.current.offsetTop,
              behavior: 'smooth',
            });
          }
        }}
        overlayOpacity={0.5}
      />
      <FormExample ref={contactFromRef} isLoaded={!!websiteData} />
      <Footer isLoaded={!!websiteData} />
    </div>
  );
}
